import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class Event extends Base implements EntityType {
    eventId: uuid;
    category: uuid;
    description?: string | null;
    duration?: number | null;
    location?: string | null;
    occurredOn: Date;
    participants: uuid[];
    allDay: boolean;
    added: uuid[];
    constructor(doc: typeof Event | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            eventId: genUUID(),
            category: null,
            occurredOn: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "event";
        // @ts-ignore
        this.eventId = doc._eventId || doc.eventId || doc.eventid || genUUID();
        // @ts-ignore
        this.category = doc._category || doc.category || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.duration = util.coalesce(util.parseFloat(doc._duration), util.parseFloat(doc.duration), util.parseFloat(doc.duration), null);
        // @ts-ignore
        this.location = doc._location || doc.location || null;
        // @ts-ignore
        this.occurredOn = DateUtil.timestampOnly(doc._occurredOn) || DateUtil.timestampOnly(doc.occurredOn) || DateUtil.timestampOnly(doc.occurredon) || null;
        // @ts-ignore
        this.participants = doc._participants || doc.participants || [];
        // @ts-ignore
        this.allDay = util.defaultBoolean([doc.allDay, doc._allDay, doc.allday, true]);
        // @ts-ignore
        this.added = doc._added || doc.added || [];
    }
    get id(): uuid { return this.eventId; }
    addParticipant(participant: uuid): uuid {
        if (!participant) throw new ValidationError('No nulls allowed in an array!');
        if (!this.participants) this.participants = [];
        this.participants.push(participant);
        return participant;
    }
    updateParticipant(participant: uuid) {
        if (!this.participants) this.participants = [];
        const index = this.participants.findIndex(item => item === participant);
        if (index !== -1) { this.participants[index] = participant; }
        else this.addParticipant(participant);
    }
    removeParticipant(participant: uuid) {
        if (!this.participants) return;
        let foundItems = this.participants?.filter(item => item === participant);
        if (foundItems && foundItems.length > 0) {
            this.participants = this.participants.filter(item => !(item === participant));
        }
    }
    abandonParticipants() { this.participants = []; }
    addAdded(added: uuid): uuid {
        if (!added) throw new ValidationError('No nulls allowed in an array!');
        if (!this.added) this.added = [];
        this.added.push(added);
        return added;
    }
    updateAdded(added: uuid) {
        if (!this.added) this.added = [];
        const index = this.added.findIndex(item => item === added);
        if (index !== -1) { this.added[index] = added; }
        else this.addAdded(added);
    }
    removeAdded(added: uuid) {
        if (!this.added) return;
        let foundItems = this.added?.filter(item => item === added);
        if (foundItems && foundItems.length > 0) {
            this.added = this.added.filter(item => !(item === added));
        }
    }
    abandonAdded() { this.added = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for the event"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.eventId; }
    static override getNonRefArrayFields(): string[] { return ["participants", "added"]; }
    static override getDefaultEntries(): Event[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'event',
    dataArea: 'Contacts',
    attachedTo: {parents: ['contact', 'lead', 'matter'], via: 'entity', onDelete: 'delete', onReplace: 'move'},
    table: 'EVENT',
    comment: 'A dated item that belongs to a Lead, a Case or a Contact through entityType and entityId, such as a date of injury, a petition served, a birthday or a court hearing. Events record when something happened, how long it lasted, where it occurred, and who was involved.',
    fields: {
        eventId: {
            type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for the event'
        },
        // The path 'contact:event:category' is an opaque stored key, not a statement about the owner. Lookup rows
        // are saved under it, and the one picker (EventCard) and the one configuration list read it for every owner,
        // so Contact, Lead and Case Events already share these categories. Renaming it or adding a second path would
        // orphan the stored lookups or split the list, so it stays. (ui/LookupSelect/LookupSelect.tsx still routes
        // 'contact:event:category' to /config/cases/event-types, a leftover label.)
        category: {
            type: 'uuid',
            entityType: 'lookup',
            lookupPaths: ['contact:event:category'],
            required: true,
            comment: 'Type of event, like a marriage or arrest'
        },
        description: {
            type: 'string', required: false, comment: 'Short description of the event'
        },
        duration: {
            type: 'float', required: false, comment: 'Duration of the event in hours; use the All Day field to mark an all-day event'
        },
        location: {
            type: 'string', required: false, comment: 'Where the event will be located'
        },
        occurredOn: {
            type: 'timestamp',
            required: true,
            comment: 'The date the event occurred'
        },
        participants: {
            type: 'uuid[]',
            entityType: 'contact',
            comment: 'The people involved with the event'
        },
        allDay: {
            type: 'boolean',
            default: true,
            comment: 'Is this an all-day event? If so, time will be 12am, otherwise we can about the time'
        },

        // Transient fields
        added: {
            type: 'uuid[]', transient: true, comment: 'Added contact IDs'
        }
    },
    maps: [
        {
            name: 'entityId', keysFn: a => a.entityId, presentFn: (a, k) => a.entityId === k && a.status !== 'D'
        },
        {name: 'category', keysFn: t => t.category, presentFn: (t, k) => t.category === k},
        {name: 'participants', keysFn: t => t.participants, presentFn: (t, k) => t.participants?.includes(k)}
    ]
});
    }
}
export default Event;
